import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { ActivitySelect, getActivityAttributes, type GetActivityAttributesProps } from '@repo/crm-atoms';
import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  Button, Card, Col, ConfigProvider, Divider, Flex, Form, Row,
  Select,
  Tooltip,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { getParentIdSelector } from 'automations/selectors/reactFlow';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type AddLeadActivityNodeData, type AddLeadActivityNodeType } from 'automations/types/nodeSchemas/actionNodes';
import { nodeSchemas } from 'automations/types/nodeTypes';
import { saveActionNode } from 'automations/utils/actionNode';
import {
  useCallback, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { ZodError } from 'zod';

import ActivityAttributeForm from './ActivityAttributeForm';
import styles from './AddLeadActivityMenu.module.scss';

type AddLeadActivityType = AddLeadActivityNodeData['additionalOptions']['addLeadActivity'];

export default function AddLeadActivityMenu({
  node,
}: {
  node: NodeProps<AddLeadActivityNodeType>
}) {
  const dispatch = useAutomationDispatch();
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);
  const [isLoading, setIsLoading] = useState(false);
  const [activityId, setActivityId] = useState<AddLeadActivityType['activityId'] | undefined>(
    node.data?.additionalOptions?.addLeadActivity?.activityId ?? undefined,
  );
  const [activityAttributes, setActivityAttributes] = useState<AddLeadActivityType['activityAttributes'] | undefined>(
    node.data?.additionalOptions?.addLeadActivity?.activityAttributes ?? undefined,
  );

  const {
    currentData: activityAttributesData,
    isFetching,
  } = useGetGraphqlDataQuery<UseQueryResult<GetActivityAttributesProps>>({
    document: getActivityAttributes,
    variables: { activityId: activityId! },
  }, {
    skip: !activityId,
  });
  const parentNodeId = useSelector(getParentIdSelector(node.id));

  const addedActivityAttributes = useMemo(() => activityAttributes?.map(
    (attribute) => attribute.schemaName,
  ) ?? [], [activityAttributes]);

  const activityAttributesOptions = useMemo(() => activityAttributesData?.activityAttribute?.map((attribute) => ({
    label: attribute.attributeName,
    value: attribute.schemaName,
  })), [activityAttributesData?.activityAttribute]);

  const attributeOptionsWithDisabled = useMemo(() => activityAttributesOptions?.map((attribute) => ({
    ...attribute,
    disabled: addedActivityAttributes.includes(attribute.value),
  })), [activityAttributesOptions, addedActivityAttributes]);

  const filteredActivityAttributesOptions = useMemo(() => attributeOptionsWithDisabled?.filter(
    (attribute) => !attribute.disabled,
  ), [attributeOptionsWithDisabled]);

  const handleAddDefaultActivityAttribute = useCallback(() => {
    setActivityAttributes((prev = []) => [
      ...prev,
      {
        value: '',
        schemaName: filteredActivityAttributesOptions?.[0]?.value ?? '',
      },
    ]);
  }, [filteredActivityAttributesOptions]);

  const handleChangeExistingKey = useCallback((oldKey: string, newKey?: string) => {
    setActivityAttributes((prev = []) => {
      if (!newKey) {
        return prev.filter((attr) => attr.schemaName !== oldKey);
      }
      return prev.map((attr) => (attr.schemaName === oldKey
        ? { ...attr, schemaName: newKey }
        : attr));
    });
  }, []);

  const updateValueAtKey = useCallback((key: string, value: string) => {
    setActivityAttributes((prev = []) => prev.map((attr) => (attr.schemaName === key ? { ...attr, value } : attr)));
  }, []);

  const disabledTooltipTitle = useMemo(() => {
    if (!activityId) {
      return translate(
        `Please select #{${TranslationEntity.activity}} type first`,
        [{ capitalize: true, withArticle: true }],
      );
    } else if (!filteredActivityAttributesOptions?.length) {
      return translate(`No #{${TranslationEntity.activity}} Attributes to add`, [{ capitalize: true }]);
    } else if (isFetching) {
      return 'Loading...';
    }
    return null;
  }, [activityId, filteredActivityAttributesOptions?.length, isFetching]);

  const handleSave = useCallback(async () => {
    setIsLoading(true);

    try {
      const parsedNodeData = nodeSchemas.AddLeadActivityActionNodeData.parse({
        ...node.data,
        additionalOptions: {
          addLeadActivity: { activityId, activityAttributes },
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedNodeData.additionalOptions,
      }));

      await dispatch(saveActionNode(node.id, parentNodeId, contentMode));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(`Failed to update #{${TranslationEntity.automation}}`);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [activityAttributes, activityId, contentMode, dispatch, node.data, node.id, parentNodeId, setMenuOpen]);

  const handleActivityChange = useCallback((value: string) => {
    setActivityId(Number(value) ?? null);
    setActivityAttributes(undefined);
  }, []);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: translate(
          `Action: Add #{${TranslationEntity.lead}} #{${TranslationEntity.activity}}`,
          [{ capitalize: true }, { capitalize: true }],
        ),
      }}
    >
      <Card title={contentMode === 'create' ? 'Set Action' : 'Edit Action'}>
        <Row>
          <Col span={6}>
            <Form.Item
              label={translate(
                `Select #{${TranslationEntity.activity}} Type`,
                [{ capitalize: true }],
              )}
              className={styles.formItem}
            />
          </Col>
          <Col span={12}>
            <ActivitySelect
              placeholder={translate(
                `Search for #{${TranslationEntity.activity}} type`,
                [{ capitalize: true }],
              )}
              showSearch
              className={styles.activitySelect}
              value={activityId?.toString()}
              onChange={handleActivityChange}
              allowClear
            />
          </Col>
        </Row>
        <Divider type="horizontal" dashed />
        <ConfigProvider componentDisabled={!activityId}>
          <Row>
            <Col span={6}>
              <Form.Item
                label={translate(
                  `Additional #{${TranslationEntity.activity}} Fields`,
                  [{ capitalize: true }],
                )}
                className={styles.formItem}
              />
            </Col>
            <Col span={18}>
              <Flex vertical gap={8}>
                {activityAttributes?.map((attribute) => (
                  <Row key={attribute.schemaName} className={styles.activityAttributeRow}>
                    <Col span={6}>
                      <Select
                        options={attributeOptionsWithDisabled}
                        value={attribute.schemaName}
                        onChange={(_value) => handleChangeExistingKey(attribute.schemaName, _value)}
                        className={styles.activitySelect}
                      />
                    </Col>
                    <Col span={13}>
                      <ActivityAttributeForm
                        attributeKey={attribute.schemaName}
                        value={attribute.value}
                        onChange={(_value) => updateValueAtKey(attribute.schemaName, _value)}
                        activityAttributes={activityAttributesData?.activityAttribute}
                      />
                    </Col>
                    <Col span={4}>
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => handleChangeExistingKey(attribute.schemaName)}
                      >
                        Remove
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Tooltip title={disabledTooltipTitle}>
                  <Button
                    disabled={Boolean(disabledTooltipTitle)}
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={handleAddDefaultActivityAttribute}
                    className={styles.addButton}
                  >
                    {translate(
                      `Add Additional #{${TranslationEntity.activity}} Fields`,
                      [{ capitalize: true }],
                    )}
                  </Button>
                </Tooltip>
              </Flex>
            </Col>
          </Row>
        </ConfigProvider>
      </Card>

      <Button
        type="primary"
        className={styles.submitButton}
        loading={isLoading}
        onClick={handleSave}
        disabled={isLoading || !activityId}
      >
        Save Action
      </Button>
    </NodeMenu>
  );
}
